/**
 * COMMERCIAL DISCLOSURE TAXONOMY — single source of truth.
 *
 * Three concepts are kept deliberately separate across the platform:
 *
 *  1. ORGANIC content        — ranked only by platform signals (searches,
 *                              filters, interests, activity, engagement).
 *                              Ranking is never purchasable.
 *  2. COMMERCIAL RELATIONSHIPS — the creator received something from an author,
 *                              publisher, PR business or vendor. Disclosed with
 *                              a pill. Does NOT alter rating or ranking.
 *  3. LITLAUNCH ADVERTISING  — placement bought from LitLaunch. NOT modelled
 *                              here at all; see components/Advertising.
 *
 * Note what is absent: there is no `paid-service` status. A customer who simply
 * paid an editor for their work is NOT giving a "Paid Review" — they get
 * `purchased`, because they bought the service. `paid-review` describes money
 * flowing TO the reviewer, and is only reachable by answering "Payment" to the
 * submission question.
 */

/** Drives the pill colour. One colour per category, learned once. */
export type DisclosureCategory =
  | 'purchase'
  | 'gifted'
  | 'paid'
  | 'interest'
  | 'organic'

export type DisclosureId =
  | 'purchased'
  | 'verified-purchase'
  | 'arc'
  | 'gifted-product'
  | 'gifted-service'
  | 'pr-copy'
  | 'paid-review'
  | 'paid-partnership'
  | 'sponsored-content'
  | 'affiliate'
  | 'own-business'
  | 'professional-relationship'
  | 'organic'

/** What is being reviewed or promoted. Drives wording and applicability. */
export type DisclosureSubjectType =
  | 'book'
  | 'audiobook'
  | 'product'
  | 'service'
  | 'graphic'

export interface DisclosureDefinition {
  id: DisclosureId
  label: string
  /** Compact label for dense surfaces (table cells, image overlays). */
  shortLabel: string
  category: DisclosureCategory
  /** Concise, neutral explanation shown on tap/hover. Never editorialising. */
  explanation: string
  /** Subject types this status can legitimately apply to. */
  appliesTo: DisclosureSubjectType[]
}

export const DISCLOSURE_DEFINITIONS: Record<
  DisclosureId,
  DisclosureDefinition
> = {
  // ── Purchase — the reader paid their own way ────────────────────────
  purchased: {
    id: 'purchased',
    label: 'Purchased',
    shortLabel: 'Purchased',
    category: 'purchase',
    explanation:
      'Reviewer bought this themselves. No free item or payment was received in exchange for this review.',
    appliesTo: ['book', 'audiobook', 'product', 'service'],
  },
  'verified-purchase': {
    id: 'verified-purchase',
    label: 'Verified Purchase',
    shortLabel: 'Verified Purchase',
    category: 'purchase',
    explanation:
      'LitLaunch has confirmed this purchase took place. This is separate from account verification.',
    appliesTo: ['book', 'audiobook', 'product', 'service'],
  },

  // ── Gifted — something was supplied free ───────────────────────────
  arc: {
    id: 'arc',
    label: 'ARC / Gifted Copy',
    shortLabel: 'ARC',
    category: 'gifted',
    explanation:
      'Reviewer received a free copy of this book. No purchase was required.',
    appliesTo: ['book', 'audiobook', 'graphic'],
  },
  'gifted-product': {
    id: 'gifted-product',
    label: 'Gifted Product',
    shortLabel: 'Gifted',
    category: 'gifted',
    explanation:
      'Reviewer received this product free of charge. No purchase was required.',
    appliesTo: ['product', 'graphic'],
  },
  'gifted-service': {
    id: 'gifted-service',
    label: 'Gifted Service',
    shortLabel: 'Gifted Service',
    category: 'gifted',
    explanation:
      'This service was provided to the reviewer free of charge. No payment was made.',
    appliesTo: ['service', 'graphic'],
  },
  'pr-copy': {
    id: 'pr-copy',
    label: 'PR / Promotional Copy',
    shortLabel: 'PR Copy',
    category: 'gifted',
    explanation:
      'Supplied as part of a PR or promotional campaign. No purchase was required.',
    appliesTo: ['book', 'audiobook', 'product', 'service', 'graphic'],
  },

  // ── Paid — money flowed TO the creator ─────────────────────────────
  'paid-review': {
    id: 'paid-review',
    label: 'Paid Review',
    shortLabel: 'Paid Review',
    category: 'paid',
    explanation:
      'Reviewer received payment to read and review this book. Payment does not indicate or guarantee a positive rating.',
    appliesTo: ['book', 'audiobook', 'product', 'service'],
  },
  'paid-partnership': {
    id: 'paid-partnership',
    label: 'Paid Partnership',
    shortLabel: 'Paid Partnership',
    category: 'paid',
    explanation:
      'The creator was paid to produce or share this content. Payment does not indicate or guarantee a positive opinion.',
    appliesTo: ['book', 'audiobook', 'product', 'service', 'graphic'],
  },
  'sponsored-content': {
    id: 'sponsored-content',
    label: 'Sponsored',
    shortLabel: 'Sponsored',
    category: 'paid',
    explanation:
      'This content is part of a paid promotional arrangement between the creator and a third party. It is not a LitLaunch advertising placement.',
    appliesTo: ['book', 'audiobook', 'product', 'service', 'graphic'],
  },

  // ── Interest — an ongoing stake in the outcome ──────────────────────
  affiliate: {
    id: 'affiliate',
    label: 'Affiliate',
    shortLabel: 'Affiliate',
    category: 'interest',
    explanation:
      'This creator may earn a commission if you buy through their links. This does not affect the rating.',
    appliesTo: ['book', 'audiobook', 'product', 'service', 'graphic'],
  },
  'own-business': {
    id: 'own-business',
    label: 'Own Product / Business',
    shortLabel: 'Own Business',
    category: 'interest',
    explanation:
      'This creator has a direct commercial interest in what they are describing.',
    appliesTo: ['book', 'audiobook', 'product', 'service', 'graphic'],
  },
  'professional-relationship': {
    id: 'professional-relationship',
    label: 'Professional Relationship',
    shortLabel: 'Professional',
    category: 'interest',
    explanation:
      'The creator has an existing working relationship with the person or business described here.',
    appliesTo: ['book', 'audiobook', 'product', 'service', 'graphic'],
  },

  // ── Organic — declared only on graphics (see note below) ────────────
  organic: {
    id: 'organic',
    label: 'Organic',
    shortLabel: 'Organic',
    category: 'organic',
    explanation:
      'Shared with no commercial relationship. Nothing was received in exchange for this post.',
    appliesTo: ['graphic'],
  },
}

/**
 * Reviews DEFAULT to organic, so an absent badge means organic and we never
 * render an "Organic" pill on a review — a badge on every card would make
 * disclosure invisible through repetition.
 *
 * Graphics DECLARE it. An unlabelled promotional graphic is genuinely
 * ambiguous: a reader cannot tell whether the uploader was asked to post it.
 */
export const SUBJECT_TYPES_SHOWING_ORGANIC: DisclosureSubjectType[] = ['graphic']

export function getDisclosure(id: DisclosureId): DisclosureDefinition {
  return DISCLOSURE_DEFINITIONS[id]
}

/** Resolves ids to definitions, dropping any that don't apply to the subject. */
export function resolveDisclosures(
  ids: DisclosureId[] | undefined,
  subjectType: DisclosureSubjectType,
): DisclosureDefinition[] {
  if (!ids || ids.length === 0) return []
  return ids
    .map((id) => DISCLOSURE_DEFINITIONS[id])
    .filter(
      (definition): definition is DisclosureDefinition =>
        Boolean(definition) && definition.appliesTo.includes(subjectType),
    )
    .filter(
      (definition) =>
        definition.id !== 'organic' ||
        SUBJECT_TYPES_SHOWING_ORGANIC.includes(subjectType),
    )
}

// ─────────────────────────────────────────────────────────────────────
// SUBMISSION QUESTION
// ─────────────────────────────────────────────────────────────────────

export type DisclosureAnswerId =
  | 'none'
  | 'free-book'
  | 'gifted-product'
  | 'gifted-service'
  | 'payment'
  | 'affiliate'
  | 'pr-arrangement'
  | 'own'
  | 'other'

export interface DisclosureAnswerOption {
  id: DisclosureAnswerId
  label: string
}

export const DISCLOSURE_QUESTION =
  'Did you receive anything in exchange for creating or sharing this?'

export const DISCLOSURE_QUESTION_EXPLANATION =
  'Disclosures help readers understand any relationship behind a review or recommendation. Receiving something does not automatically make a review positive or negative.'

export const DISCLOSURE_ANSWER_OPTIONS: DisclosureAnswerOption[] = [
  { id: 'none', label: 'No' },
  { id: 'free-book', label: 'Free book / ARC' },
  { id: 'gifted-product', label: 'Gifted product' },
  { id: 'gifted-service', label: 'Gifted service' },
  { id: 'payment', label: 'Payment' },
  { id: 'affiliate', label: 'Affiliate commission' },
  { id: 'pr-arrangement', label: 'PR/promotional arrangement' },
  { id: 'own', label: 'This is my own book/product/business' },
  { id: 'other', label: 'Other commercial relationship' },
]

/**
 * Answer -> badge mapping, defined PER SUBJECT TYPE rather than assumed to be
 * universal. "Payment" on a book review yields Paid Review; the same answer on
 * a graphic yields Paid Partnership, because the creator was paid for the
 * content rather than for reading something.
 */
const ANSWER_MAP: Record<
  DisclosureSubjectType,
  Partial<Record<DisclosureAnswerId, DisclosureId>>
> = {
  book: {
    'free-book': 'arc',
    'gifted-product': 'gifted-product',
    'gifted-service': 'gifted-service',
    payment: 'paid-review',
    affiliate: 'affiliate',
    'pr-arrangement': 'pr-copy',
    own: 'own-business',
    other: 'professional-relationship',
  },
  audiobook: {
    'free-book': 'arc',
    'gifted-product': 'gifted-product',
    'gifted-service': 'gifted-service',
    payment: 'paid-review',
    affiliate: 'affiliate',
    'pr-arrangement': 'pr-copy',
    own: 'own-business',
    other: 'professional-relationship',
  },
  product: {
    'free-book': 'gifted-product',
    'gifted-product': 'gifted-product',
    'gifted-service': 'gifted-service',
    payment: 'paid-review',
    affiliate: 'affiliate',
    'pr-arrangement': 'pr-copy',
    own: 'own-business',
    other: 'professional-relationship',
  },
  service: {
    'free-book': 'gifted-product',
    'gifted-product': 'gifted-product',
    'gifted-service': 'gifted-service',
    payment: 'paid-review',
    affiliate: 'affiliate',
    'pr-arrangement': 'pr-copy',
    own: 'own-business',
    other: 'professional-relationship',
  },
  graphic: {
    'free-book': 'arc',
    'gifted-product': 'gifted-product',
    'gifted-service': 'gifted-service',
    // A PR professional paid by a client discloses a paid partnership. This is
    // legitimate work, and is NOT the same as paying LitLaunch for placement.
    payment: 'paid-partnership',
    affiliate: 'affiliate',
    'pr-arrangement': 'pr-copy',
    own: 'own-business',
    other: 'professional-relationship',
  },
}

/**
 * Converts submission answers into the public badge set. Answering "No"
 * produces no badges on a review, and an explicit Organic badge on a graphic.
 */
export function disclosuresFromAnswers(
  answers: DisclosureAnswerId[],
  subjectType: DisclosureSubjectType,
): DisclosureId[] {
  if (answers.length === 0 || answers.includes('none')) {
    return SUBJECT_TYPES_SHOWING_ORGANIC.includes(subjectType)
      ? ['organic']
      : []
  }
  const map = ANSWER_MAP[subjectType]
  const ids = answers
    .map((answer) => map[answer])
    .filter((id): id is DisclosureId => Boolean(id))
  return Array.from(new Set(ids))
}

// ─────────────────────────────────────────────────────────────────────
// READER-FACING FILTERS
// ─────────────────────────────────────────────────────────────────────

export interface DisclosureFilter {
  id: string
  label: string
  /** Empty means "match everything", i.e. the All option. */
  matches: DisclosureId[]
}

/**
 * Filters are a lens, not a gate. "All" is the default and INCLUDES paid,
 * gifted and ARC reviews — nothing is hidden by default.
 */
export const DISCLOSURE_FILTERS: DisclosureFilter[] = [
  { id: 'all', label: 'All', matches: [] },
  { id: 'purchased', label: 'Purchased', matches: ['purchased'] },
  {
    id: 'verified-purchase',
    label: 'Verified Purchase',
    matches: ['verified-purchase'],
  },
  {
    id: 'gifted',
    label: 'ARC / Gifted',
    matches: ['arc', 'gifted-product', 'gifted-service', 'pr-copy'],
  },
  {
    id: 'paid',
    label: 'Paid',
    matches: ['paid-review', 'paid-partnership', 'sponsored-content'],
  },
  { id: 'affiliate', label: 'Affiliate', matches: ['affiliate'] },
]

export function matchesDisclosureFilter(
  disclosures: DisclosureId[] | undefined,
  filterId: string,
): boolean {
  if (filterId === 'all') return true
  const filter = DISCLOSURE_FILTERS.find((f) => f.id === filterId)
  if (!filter || filter.matches.length === 0) return true
  if (!disclosures) return false
  return disclosures.some((id) => filter.matches.includes(id))
}
